import { useState } from "react";
import { Send, Copy } from "lucide-react";
import { Button } from "../../components/Button";
import { useToast } from "../../components/Toast";
import { api } from "../../api/client";
import type { SignoffInvite as Invite } from "../../api/types";

// 報價單上的「請客戶回簽」(第 14 輪)。
//
// --------------------------------------------------------------------------
// 連結與臨時密碼**顯示在這裡**
// --------------------------------------------------------------------------
// 兩者都不進 email_log 的內文(資料庫裡只有佔位字串);它們在按下按鈕的那一刻回給
// 按的人。寄出去的那封原信(含兩者)另存在按的人自己的信件備份,只有他在
// 「信箱 → 系統寄出」讀得到(2026-10-05 業主決定不遮蔽)。
//
// 而這不只是安全考量,也是**現在唯一走得通的一條路**:寄信卡在寄件網域的
// 信譽(見 docs/寄信設定現況.md),Gmail 在 SMTP 階段直接拒收。
// 在新網域來之前,業務就是靠這一格把連結唸給客戶。
//
// 換頁之後這一格就不見了,但本人可以到「系統寄出」重看那封信;
// 重按會作廢舊的那一份並發新的,而那正是「我重寄了一份」該有的意思。

export interface SignoffInviteProps {
  quoteId: number;
  /** 唯讀(封存 / 財務)時整顆鈕反灰。 */
  disabled?: boolean;
}

export function SignoffInvite({ quoteId, disabled = false }: SignoffInviteProps) {
  const [invite, setInvite] = useState<Invite | null>(null);
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  const send = async () => {
    setBusy(true);
    try {
      const got = await api.post<Invite>(`/quotes/${quoteId}/signoff-invite`, {});
      setInvite(got);
      toast.push("已產生回簽連結", "success");
    } catch (err) {
      toast.push(err instanceof Error ? err.message : "產生不了", "error");
    } finally {
      setBusy(false);
    }
  };

  const copy = async (text: string, what: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.push(`已複製${what}`, "success");
    } catch {
      // 剪貼簿在非 https 或沒授權時會擋 —— 那不是錯誤,文字就在畫面上,
      // 使用者自己選起來複製一樣有效。
      toast.push("複製不了,請手動選取畫面上的文字", "error");
    }
  };

  return (
    <section className="flex flex-col gap-2" aria-label="請客戶回簽">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="m-0 text-base font-semibold text-[var(--text)]">請客戶回簽</h3>
        <Button
          size="sm"
          icon={<Send aria-hidden="true" size={14} />}
          loading={busy}
          disabled={disabled}
          title={disabled ? "封存的單是唯讀的" : undefined}
          onClick={() => void send()}
        >
          {invite ? "重新產生" : "產生連結與臨時密碼"}
        </Button>
      </div>

      {invite === null ? (
        <p className="m-0 text-sm text-[var(--text-3)]">
          按一下會替客戶開一組帳號,產生回簽連結與臨時密碼,並寄一封信給{" "}
          {"報價單上的乙方窗口"}。客戶可以線上簽,也可以列印簽名後掃描回傳。
        </p>
      ) : (
        <div className="flex flex-col gap-2 rounded-[var(--radius)] border border-[var(--accent)] bg-[var(--surface)] p-3">
          <p className="m-0 text-sm text-[var(--text-2)]">
            已寄給 <strong>{invite.sent_to_email}</strong>
            {invite.expires_at ? `,${invite.expires_at.slice(0, 10)} 前有效` : ""}。
          </p>
          {/* 見檔頭:寄信目前卡在網域信譽,所以這兩格是真正走得通的那條路。 */}
          <p className="m-0 text-xs text-[var(--text-3)]">
            離開這一頁後,可以到「信箱 → 系統寄出」再看這封信(含連結與臨時密碼,只有你自己看得到)。
            寄信目前可能被對方的信箱擋下,請直接把下面兩項給客戶。
          </p>

          <label className="flex flex-col gap-1 text-sm">
            <span className="text-xs text-[var(--text-2)]">回簽連結</span>
            <span className="flex flex-wrap items-center gap-2">
              <input
                readOnly
                aria-label="回簽連結"
                value={invite.link}
                className="h-9 min-w-0 flex-1 rounded-[var(--radius)] border border-[var(--border-strong)] bg-[var(--surface-2)] px-2 text-sm text-[var(--text)]"
              />
              <Button
                size="sm"
                icon={<Copy aria-hidden="true" size={14} />}
                onClick={() => void copy(invite.link, "連結")}
              >
                複製
              </Button>
            </span>
          </label>

          <label className="flex flex-col gap-1 text-sm">
            <span className="text-xs text-[var(--text-2)]">臨時密碼</span>
            <span className="flex flex-wrap items-center gap-2">
              <input
                readOnly
                aria-label="臨時密碼"
                value={invite.temp_password}
                className="h-9 min-w-0 flex-1 rounded-[var(--radius)] border border-[var(--border-strong)] bg-[var(--surface-2)] px-2 font-mono text-sm text-[var(--text)]"
              />
              <Button
                size="sm"
                icon={<Copy aria-hidden="true" size={14} />}
                onClick={() => void copy(invite.temp_password, "臨時密碼")}
              >
                複製
              </Button>
            </span>
          </label>

          <p className="m-0 text-xs text-[var(--text-3)]">
            客戶登入時要輸入公司統一編號、這個信箱,以及上面這組臨時密碼;
            進去之後系統會請他改成自己的密碼。
          </p>
        </div>
      )}
    </section>
  );
}
